// Catálogo de advertencias de la verificación contable de nómina.
// Vive aparte para que lo puedan usar tanto la pantalla como el PDF sin importarse
// entre sí. Cada detalle describe la consecuencia real en el timbrado.
//
// `informativa: true` marca las que sólo describen cómo está configurado el
// concepto y no hay nada que corregir: no cuentan para el filtro "Sólo con
// problemas" ni para el contador de problemas.
const ADVERTENCIAS = {
  CONCEPTO_INFORMATIVO: {
    texto: "Informativo",
    color: "purple",
    informativa: true,
    detalle:
      "El concepto tiene encendido «No Suma»: al timbrar se descarta antes de revisar sus cuentas, así que nunca genera cargo ni abono. Por eso no captura cuentas contables.",
  },
  CONCEPTO_NO_CONTABILIZA: {
    texto: "No contabiliza",
    color: "purple",
    informativa: true,
    detalle:
      "El concepto tiene encendido «No contabilizar»: al timbrar no genera ningún movimiento en la póliza de nómina y tampoco crea subcuentas por empleado. Se usa cuando el gasto ya se registró por otro lado — la factura del proveedor de los vales o la salida de almacén — para no duplicarlo. Por eso no captura cuentas contables.",
  },
  INFORMATIVO_GENERA_CUENTAS: {
    texto: "Informativo con cuentas por empleado",
    color: "red",
    detalle:
      "Es un concepto informativo («No Suma») pero tiene encendido «Genera cuenta por empleado» con cuenta de pasivo. Al timbrar se le crean subcuentas contables reales a cada empleado y se consume consecutivo, aunque el concepto nunca aparece en la póliza. Conviene apagar esa bandera desde Editar Concepto.",
  },
  SIN_CUENTAS: {
    texto: "Sin cuentas",
    color: "red",
    detalle:
      "El concepto no tiene cuenta de costo/gasto ni de pasivo: al timbrar se omite por completo de la póliza.",
  },
  SIN_CUENTA_COSTO_GASTO: {
    texto: "Falta costo/gasto",
    color: "volcano",
    detalle:
      "Percepciones y Obligaciones cargan a su cuenta de costo o gasto. Sin ella no se genera el cargo, sólo el abono al pasivo.",
  },
  SIN_CUENTA_PASIVO: {
    texto: "Falta pasivo",
    color: "volcano",
    detalle: "Sin cuenta de pasivo no se genera el abono del concepto en la póliza.",
  },
  GENERA_SIN_PASIVO: {
    texto: "Genera sin pasivo",
    color: "orange",
    detalle:
      "Está encendido «Generar cuenta por empleado» pero no hay cuenta de pasivo de la cual colgar la subcuenta: la bandera no hace nada.",
  },
  CUENTA_INEXISTENTE: {
    texto: "Cuenta inexistente",
    color: "red",
    detalle:
      "Una de las cuentas configuradas ya no existe en el catálogo de esta empresa. Hay que volver a seleccionarla.",
  },
  EMPLEADOS_SIN_CUENTA: {
    texto: "Empleados sin cuenta",
    color: "gold",
    informativa: true,
    detalle:
      "Hay empleados a los que aplica el concepto y todavía no tienen subcuenta. Se les crea automáticamente la primera vez que se les timbre el concepto con importe; mientras venga en cero no se genera nada, así que en conceptos de captura manual (pensión alimenticia, préstamo, caja de ahorro) es normal que esto se quede encendido.",
  },
  LIMITE_CONSECUTIVO: {
    texto: "Consecutivo agotado",
    color: "red",
    detalle:
      "Ya no hay códigos libres bajo esa cuenta de pasivo (el nivel se queda sin dígitos). El timbrado va a fallar con un error hasta que se use otra cuenta mayor o se ajuste el consecutivo.",
  },
  OBLIGACION_DESACTIVADA: {
    texto: "Obligaciones desactivadas",
    color: "geekblue",
    informativa: true,
    detalle:
      "En Configuración General está encendido «Desactivar movimientos de las obligaciones», así que las cuentas de este concepto no se usan al timbrar.",
  },

  // ── Agrupación contable («Se contabiliza en») ─────────────────────────────
  CONTABILIZA_EN_OTRO: {
    texto: "Se contabiliza en otro",
    color: "cyan",
    informativa: true,
    detalle:
      "Este concepto no genera renglones propios en la póliza: sus importes se suman a los del concepto en el que está agrupado, que pone la cuenta de costo/gasto, la de pasivo y el texto del movimiento. Sus cuentas propias se conservan guardadas pero no se usan. El recibo, el CFDI y los acumulados siguen viéndolo como un concepto aparte.",
  },
  ABSORBE_CONCEPTOS: {
    texto: "Absorbe otros conceptos",
    color: "cyan",
    informativa: true,
    detalle:
      "Hay otros conceptos configurados para contabilizarse aquí: sus importes se suman a los de éste y salen en un solo movimiento por proyecto, con las cuentas y el texto de este concepto.",
  },
  SUBCUENTAS_SIN_USO: {
    texto: "Subcuentas sin uso",
    color: "gold",
    detalle:
      "El concepto está agrupado en otro, pero ya tiene subcuentas por empleado asignadas de cuando se contabilizaba solo. A partir de ahora se usan las del concepto principal y éstas dejan de recibir movimientos, conservando el saldo que traigan. Si ese saldo no es cero hay que traspasarlo a mano.",
  },
  AGRUPADO_INEXISTENTE: {
    texto: "Agrupado en concepto inexistente",
    color: "red",
    detalle:
      "El concepto en el que se iba a contabilizar ya no existe. Al timbrar se ignora la agrupación y este concepto usa sus propias cuentas.",
  },
  AGRUPADO_EN_OTRO_TIPO: {
    texto: "Agrupado en otro tipo",
    color: "red",
    detalle:
      "Sólo se puede agrupar en un concepto del mismo tipo: mezclar una deducción con una percepción rompería el prorrateo de deducciones sobre la provisión y el signo del movimiento. Al timbrar se ignora la agrupación y este concepto usa sus propias cuentas.",
  },
  AGRUPADO_EN_CADENA: {
    texto: "Agrupado en cadena",
    color: "red",
    detalle:
      "El concepto en el que se iba a contabilizar está agrupado a su vez en otro. Sólo se permite un nivel, así que al timbrar se ignora la agrupación y este concepto usa sus propias cuentas.",
  },
  AGRUPADO_EN_INFORMATIVO: {
    texto: "Agrupado en informativo",
    color: "red",
    detalle:
      "El concepto en el que se iba a contabilizar tiene encendido «No Suma», o sea que nunca aparece en la póliza: agrupar ahí haría desaparecer estos importes. Al timbrar se ignora la agrupación y este concepto usa sus propias cuentas.",
  },
  AGRUPADO_SIENDO_PRESTAMO: {
    texto: "Préstamo agrupado",
    color: "red",
    detalle:
      "Este concepto entrega un préstamo, así que su cargo va a la cuenta por cobrar del empleado y no a una cuenta de gasto: agruparlo lo mandaría al gasto del otro concepto y le quitaría la subcuenta compartida. Al timbrar se ignora la agrupación. Quítale «Se contabiliza en».",
  },
  AGRUPADO_EN_PRESTAMO: {
    texto: "Agrupado en un préstamo",
    color: "red",
    detalle:
      "El concepto en el que se iba a contabilizar entrega un préstamo, y sus cuentas son una cuenta por cobrar: absorber aquí cargaría, por ejemplo, un sueldo contra el préstamo del empleado. Al timbrar se ignora la agrupación y este concepto usa sus propias cuentas.",
  },

  // ── Préstamo entregado como percepción ────────────────────────────────────
  PRESTAMO_NO_MARCADO: {
    texto: "Parece préstamo sin marcar",
    color: "volcano",
    detalle:
      "La cuenta de costo o gasto de esta percepción es la misma cuenta que una deducción abona con subcuenta por empleado, o sea que es una cuenta por cobrar y no un gasto. Así, el cargo de la entrega se queda en la cuenta mayor mientras los abonos del cobro van a la subcuenta del empleado: esa subcuenta acumula puros abonos, queda con saldo acreedor y el saldo del préstamo por persona no se puede leer. Enciende «Préstamo entregado en la nómina» y liga el concepto a la deducción que lo cobra; también lo deja configurado el paso de contabilidad del asistente de nómina.",
  },
  PRESTAMO_SIN_COBRO: {
    texto: "Préstamo sin concepto de cobro",
    color: "orange",
    detalle:
      "Está encendido «Préstamo entregado en la nómina» pero no se indicó qué deducción lo cobra, y de ahí sale la cuenta por cobrar. Al timbrar, el cargo va a la cuenta de costo o gasto de este concepto y se queda en la cuenta mayor, sin subcuenta por empleado, así que el saldo del préstamo no se puede seguir por persona.",
  },
  PRESTAMO_COBRO_INVALIDO: {
    texto: "Concepto de cobro inválido",
    color: "red",
    detalle:
      "El concepto que iba a cobrar el préstamo no sirve: ya no existe, no es una deducción, es informativo («No Suma») o no tiene cuenta de pasivo. Al timbrar se ignora el enlace y el cargo va a la cuenta propia de este concepto, sin subcuenta por empleado.",
  },
  PRESTAMO_COBRO_SIN_SUBCUENTA: {
    texto: "Préstamo sin cuenta por empleado",
    color: "gold",
    informativa: true,
    detalle:
      "El concepto que cobra el préstamo no tiene encendido «Genera cuenta por empleado», así que la entrega y los cobros caen en la cuenta mayor. La póliza cuadra y el saldo global del préstamo es correcto, pero no se puede leer cuánto debe cada persona. Es una configuración válida: para el seguimiento por empleado, enciende esa bandera en el concepto de cobro.",
  },
};

// Advertencias que sí exigen que alguien haga algo (las que no están marcadas
// como informativas).
export const tieneProblema = (claves = []) =>
  claves.some((clave) => ADVERTENCIAS[clave] && !ADVERTENCIAS[clave].informativa);

export const TIPO_LABELS = {
  1: { label: "Percepción", color: "green" },
  2: { label: "Deducción", color: "red" },
  3: { label: "Obligación", color: "blue" },
};

export default ADVERTENCIAS;
